import { closedPOStatuses, openPOStatuses } from "~/constants/purchaseOrders"
import { StatusColor } from "~/constants/status"
import { sortSizesComparator } from "~/utils/sizeSplit"

// Quantity is locked once a PO has been sent to the supplier and onwards.
const QUANTITY_LOCKED_STATUSES = [...openPOStatuses, ...closedPOStatuses]

export function isQuantityLockedPOStatus(status: string | null | undefined): boolean {
  return status != null && QUANTITY_LOCKED_STATUSES.includes(status)
}

// Narrower "committed draft" set: POs the user has explicitly promoted to a real draft.
// Excludes `pre_draft` on purpose — those are transitional and should not be counted as
// drafts in summaries/reporting/server-side branching. For the broader set used by UI
// listings (the Drafts tab and its count), see `draftTabPOStatuses` in
// `~/constants/purchaseOrders`.
export const COMMITTED_DRAFT_STATUSES = ["draft", "draft_approved"] as const
export const PRE_DRAFT_STATUSES = ["pre_draft"] as const

/**
 * The committed statuses whose quantity is real, placed supply. Both halves of the "Already
 * Purchased" figure read THIS — `purchasePlan.getPurchasedQuantities`' `sentQty` and the
 * `sentPurchaseOrders` list the hover card sums under it — so neither can be the complement of the
 * draft set and drift from the other.
 */
export const SENT_PO_STATUSES: readonly string[] = ["sent"]

/**
 * Statuses a purchase order must have to count as a commitment against a supply plan: sent, plus
 * the committed drafts. Both supply-plan reads — `purchasePlan.getPurchasedQuantities` and
 * `purchasePlanPurchaseOrder.getLinesBySkuWarehouse` — filter on THIS list and pair it with
 * `archivedStatus is null`. They used to spell `status = 'sent'` out separately, and the hover
 * breakdown was derived by subtracting one from the other, so any drift between them produced a
 * wrong "from incoming stock" number on screen.
 *
 * A committed status that is NOT a draft (`registered`, say) has to be added to
 * {@link SENT_PO_STATUSES} as well, or its lines are read here and then counted by neither half.
 */
export const COMMITTED_PO_STATUSES: readonly string[] = [
  ...SENT_PO_STATUSES,
  ...COMMITTED_DRAFT_STATUSES,
]

export function isCommittedDraftStatus(status: string): boolean {
  return (COMMITTED_DRAFT_STATUSES as readonly string[]).includes(status)
}

export function isPreDraftStatus(status: string): boolean {
  return (PRE_DRAFT_STATUSES as readonly string[]).includes(status)
}

export function poStatusToColor(status: string): StatusColor {
  switch (status) {
    case "pre_draft":
      return StatusColor.Neutral
    case "draft":
    case "draft_approved":
      return StatusColor.Warning
    case "sent":
      return StatusColor.Information
    case "registered":
      return StatusColor.Information
    case "delivered":
      return StatusColor.Success
    default:
      return StatusColor.Neutral
  }
}

export function poStatusLabel(status: string): string {
  if (status === "pre_draft") return "Pre-draft"
  if (status === "draft_approved") return "Draft - Approved"
  return status.charAt(0).toUpperCase() + status.slice(1)
}

export function sortPurchaseOrderItems<
  T extends { sku: { productId: string | null; variantId: string | null; size: string | null } },
>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const prodA = a.sku.productId ?? ""
    const prodB = b.sku.productId ?? ""
    if (prodA !== prodB) return prodA.localeCompare(prodB)

    const varA = a.sku.variantId ?? ""
    const varB = b.sku.variantId ?? ""
    if (varA !== varB) return varA.localeCompare(varB)

    return sortSizesComparator(a.sku.size ?? "", b.sku.size ?? "")
  })
}
